import { AlkLogger } from '@alk/core';
import { injectable } from 'inversify';
import { IPrice } from '@fnx/fx-pad';
import { IPriceService, ISpotQuote, SpotPriceRequest } from '../../../interfaces';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { Observable, ReplaySubject, Subject, timer } from 'rxjs';

const updateFrequency = 1500;

const randomId = () => Math.floor(Math.random() * 1_000_000_000);

@injectable()
export class EspPriceMock implements IPriceService {
    private readonly request$: ReplaySubject<SpotPriceRequest> = new ReplaySubject<SpotPriceRequest>(1);
    private readonly destroy$: Subject<boolean> = new Subject<boolean>();
    private quoteObj$: Observable<ISpotQuote>;

    quoteObservable():  Observable<ISpotQuote> {
        return Boolean(this.quoteObj$) ?
            this.quoteObj$ :
            this.quoteObj$ = this.request$.pipe(
                takeUntil(this.destroy$),
                map(request => [ request, randomId() ]),
                switchMap(([ request, id ]: [ SpotPriceRequest, number ]) => {
                    return timer(
                        updateFrequency,
                        (Math.floor(Math.random() * 40) + 1) * 100)
                        .pipe(
                            map(() => this.generatePrices(request, id)),
                        );
                }));
    }

    stop(): void {
        this.destroy$.next(true);

        AlkLogger.debug('[ESP_PRICE_MOCK] stop');
    }

    private generatePrices(request: SpotPriceRequest, requestId: number): ISpotQuote {
        const quantity = request.quantity || 1;
        const price = this.getRandomPrice(request.ccyPair, quantity);
        return {
            requestId,
            askPrice: price.askPrice,
            bidPrice: price.bidPrice,
        } as ISpotQuote;
    }

    private getRandomPrice(instrument: string, quantity) {
        let rate = 0;
        let askRate = 0;
        let bidRate = 0;
        let priceSettings: IPrice = null;
        switch (instrument) {
            default:
            case 'EURUSD':
                rate = (1.08385 + Math.random() / 10000 + Math.log(quantity) / 10000);
                askRate = rate + (Math.log(quantity) / 10000) + (Math.random() / 100000);
                bidRate = rate - (Math.log(quantity) / 10000) + (Math.random() / 100000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 2,
                    FractionSize: 4,
                };
                break;
            case 'EURGBP':
                rate = (0.87064 + Math.random() / 10000 + Math.log(quantity) / 10000);
                askRate = rate + (Math.log(quantity) / 100000) + (Math.random() / 100000);
                bidRate = rate - (Math.log(quantity) / 100000) + (Math.random() / 100000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 2,
                    FractionSize: 3,
                };

                break;
            case 'USDJPY':
                rate = 106.56 + Math.random() / 10 + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random() / 100);
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random() / 100);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 0,
                    FractionSize: 2,
                };

                break;
            case 'USDRUB':
                rate = 73.7768 + Math.random() / 100 + Math.log(quantity) / 10000;
                askRate = rate + (Math.log(quantity) / 1000) + (Math.random() / 1000);
                bidRate = rate - (Math.log(quantity) / 1000) + (Math.random() / 1000);

                priceSettings = {
                    Value: null,
                    ExtraDigits: 0,
                    FractionSize: 4,
                };
                break;
        }

        const askPrice = {
            ...priceSettings,
            Value: askRate,
        };
        const bidPrice = {
            ...priceSettings,
            Value: bidRate,
        };

        return {
            askPrice: askPrice,
            bidPrice: bidPrice,
        };
    }

    request(request: SpotPriceRequest): boolean {
        AlkLogger.debug('[ESP_PRICE_MOCK] updated');

        this.request$.next(request);

        return true;
    }

}
